/**
 * Centralized path configuration for file operations.
 *
 * This file mirrors the backend path config (ui-file-browser/config/paths.js)
 * to ensure consistency across frontend and backend.
 *
 * IMPORTANT: Keep this file in sync with the backend config.
 *
 * Directory Structure:
 * /data/rooms/{roomId}/
 * ├── uploads/                     # User-uploaded content
 * │   ├── caller/                  # Caller uploads (restricted)
 * │   └── officer/                 # Officer uploads
 * ├── video_audio/                 # All recordings
 * ├── canvas/                      # Canvas snapshot exports (officer-generated)
 * ├── map/                         # Map screenshot exports (officer-generated)
 * └── generated_reports/           # Report editor output
 */

// Root structure directories
export const FILE_PATHS = {
  // Root directories
  UPLOADS: 'uploads',
  VIDEO_AUDIO: 'video_audio',
  GENERATED_REPORTS: 'generated_reports',

  // Upload subdirectories
  UPLOADS_CALLER: 'uploads/caller',
  UPLOADS_OFFICER: 'uploads/officer',

  // Interactive-tool exports (officer-generated PNG snapshots).
  // CanvasTab / MapTab upload here via /api/upload-file with source=officer.
  CANVAS: 'canvas',
  MAP: 'map',
} as const;

// Type for all path values
export type FilePath = (typeof FILE_PATHS)[keyof typeof FILE_PATHS];

// Access control configuration
export const CALLER_ALLOWED_PATHS = [
  `/${FILE_PATHS.UPLOADS_CALLER}`,
  `/${FILE_PATHS.VIDEO_AUDIO}`,
] as const;

export const CALLER_HIDDEN_PATHS = [
  `/${FILE_PATHS.GENERATED_REPORTS}`,
  `/${FILE_PATHS.UPLOADS_OFFICER}`,
] as const;

// Default upload destinations
export const CALLER_DEFAULT_UPLOAD = `/${FILE_PATHS.UPLOADS_CALLER}`;
export const OFFICER_DEFAULT_UPLOAD = `/${FILE_PATHS.UPLOADS_OFFICER}`;

// Legacy path mappings for backward compatibility
export const LEGACY_MAPPINGS: Record<string, string> = {
  '/caller-uploads': `/${FILE_PATHS.UPLOADS_CALLER}`,
  '/reports': `/${FILE_PATHS.GENERATED_REPORTS}`,
};

/**
 * Upload destination preference type.
 * - 'officer': Always upload to officer uploads folder
 * - 'current': Upload to the current directory being viewed
 */
export type UploadDestination = 'officer' | 'current';

/**
 * Get all directories that should be created for a new call.
 * @returns Array of directory paths (relative to room root)
 */
export function getCallDirectories(): string[] {
  return [
    FILE_PATHS.UPLOADS,
    FILE_PATHS.UPLOADS_CALLER,
    FILE_PATHS.UPLOADS_OFFICER,
    FILE_PATHS.VIDEO_AUDIO,
    FILE_PATHS.CANVAS,
    FILE_PATHS.MAP,
    FILE_PATHS.GENERATED_REPORTS,
  ];
}

/**
 * Interactive-tool export folders (canvas / map snapshots). These hold
 * officer-generated PNG exports that should surface in the unified attachment
 * list alongside uploads + recordings.
 */
export const INTERACTIVE_EXPORT_PATHS = [
  `/${FILE_PATHS.CANVAS}`,
  `/${FILE_PATHS.MAP}`,
] as const;

/**
 * Map a legacy path to the new path structure.
 * @param requestPath - The incoming request path
 * @returns The mapped path (or original if no mapping exists)
 */
export function mapLegacyPath(requestPath: string): string {
  // Normalize path to start with /
  const normalizedPath = requestPath.startsWith('/') ? requestPath : `/${requestPath}`;

  // Check for exact legacy path match
  for (const [legacyPath, newPath] of Object.entries(LEGACY_MAPPINGS)) {
    if (normalizedPath === legacyPath || normalizedPath.startsWith(`${legacyPath}/`)) {
      return normalizedPath.replace(legacyPath, newPath);
    }
  }

  return normalizedPath;
}

/**
 * Check if a path should be hidden from callers.
 * @param filePath - The file path to check
 * @returns True if the path should be hidden from callers
 */
export function isCallerHiddenPath(filePath: string): boolean {
  const normalizedPath = filePath.startsWith('/') ? filePath : `/${filePath}`;

  return CALLER_HIDDEN_PATHS.some(
    hiddenPath =>
      normalizedPath === hiddenPath || normalizedPath.startsWith(`${hiddenPath}/`)
  );
}

/**
 * Check if a caller is allowed to access a path.
 * @param filePath - The file path to check
 * @param allowedPaths - The paths the caller is allowed to access
 * @returns True if access is allowed
 */
export function isCallerAllowedPath(
  filePath: string,
  allowedPaths: readonly string[] = CALLER_ALLOWED_PATHS
): boolean {
  const normalizedPath = filePath.startsWith('/') ? filePath : `/${filePath}`;

  // Root path is allowed for listing (but individual files will be filtered)
  if (normalizedPath === '/' || normalizedPath === '') {
    return true;
  }

  return allowedPaths.some(
    allowedPath =>
      normalizedPath === allowedPath || normalizedPath.startsWith(`${allowedPath}/`)
  );
}

/**
 * Build the full path for a recording file.
 * @param roomId - The room identifier
 * @param filename - The recording filename
 * @returns Full path for the recording
 */
export function getRecordingPath(roomId: string, filename: string): string {
  return `${roomId}/${FILE_PATHS.VIDEO_AUDIO}/${filename}`;
}

/**
 * Build the full path for a generated report.
 * @param roomId - The room identifier
 * @param filename - The report filename
 * @returns Full path for the report
 */
export function getReportPath(roomId: string, filename: string): string {
  return `${roomId}/${FILE_PATHS.GENERATED_REPORTS}/${filename}`;
}

/**
 * Build the full path for a caller upload.
 * @param roomId - The room identifier
 * @param filename - The upload filename
 * @returns Full path for the caller upload
 */
export function getCallerUploadPath(roomId: string, filename: string): string {
  return `${roomId}/${FILE_PATHS.UPLOADS_CALLER}/${filename}`;
}

/**
 * Build the full path for an officer upload.
 * @param roomId - The room identifier
 * @param filename - The upload filename
 * @returns Full path for the officer upload
 */
export function getOfficerUploadPath(roomId: string, filename: string): string {
  return `${roomId}/${FILE_PATHS.UPLOADS_OFFICER}/${filename}`;
}
